import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Check, Hourglass, Ticket, QrCode, Copy, XCircle, WhatsappLogo, UploadSimple, FileImage } from "@phosphor-icons/react";
import { API, authHeaders } from "@/lib/api";
import { useLocale } from "@/i18n";
import { formatPrice } from "../booking/format";

export default function BillingTab({ info, onChanged }) {
  const { t, locale } = useLocale();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [pix, setPix] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);

  const { subscription_status, subscription_active, trial_days_left, paid_days_left, plan, country, payment_info } = info;
  const isPy = country === "py";
  const paid = ["active", "trialing"].includes(subscription_status);
  const viaCode = !paid && paid_days_left > 0;

  const [proofs, setProofs] = useState([]);
  const [uploadingProof, setUploadingProof] = useState(false);

  const loadProofs = useCallback(async () => {
    if (!isPy) return;
    try {
      const res = await axios.get(`${API}/barber/payment-proofs`, { headers: authHeaders() });
      setProofs(res.data);
    } catch {
      // silencioso
    }
  }, [isPy]);

  useEffect(() => {
    loadProofs();
  }, [loadProofs]);

  const copyAlias = async () => {
    try {
      await navigator.clipboard.writeText(payment_info.alias);
      toast.success(t("py_pay_alias_copied"));
    } catch {
      toast.info(payment_info.alias);
    }
  };

  const uploadProof = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingProof(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/barber/payment-proof`, fd, { headers: authHeaders() });
      toast.success(t("py_proof_sent"));
      loadProofs();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(err.response?.status === 400 ? t("py_proof_invalid") : typeof detail === "string" ? detail : t("py_proof_error"));
    } finally {
      setUploadingProof(false);
    }
  };

  const STATUS_LABELS = {
    active: t("bill_active"),
    trialing: t("bill_trial_card"),
    past_due: t("bill_past_due"),
    canceled: t("bill_canceled"),
    none: viaCode ? t("bill_active_code") : t("bill_trial"),
  };

  const subscribe = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/barber/billing/checkout`,
        { origin_url: window.location.origin },
        { headers: authHeaders() }
      );
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error(t("book_error"));
      setLoading(false);
    }
  };

  const startPix = async () => {
    setPixLoading(true);
    try {
      const res = await axios.post(`${API}/barber/billing/pix`, {}, { headers: authHeaders() });
      setPix(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("pix_error"));
    } finally {
      setPixLoading(false);
    }
  };

  useEffect(() => {
    if (!pix) return;
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/barber/billing/pix/${pix.order_id}`, { headers: authHeaders() });
        if (res.data.paid) {
          clearInterval(timer);
          setPix(null);
          toast.success(t("pix_paid"));
          onChanged();
        }
      } catch {
        // mantém tentando
      }
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pix]);

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(pix.qr_code);
      toast.success(t("pix_copied"));
    } catch {
      toast.info(pix.qr_code);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (code.trim().length < 6) return toast.warning(t("redeem_error"));
    setRedeeming(true);
    try {
      const res = await axios.post(`${API}/barber/redeem`, { code }, { headers: authHeaders() });
      toast.success(`${t("redeem_ok")} (+${res.data.days}d)`);
      setCode("");
      onChanged();
    } catch {
      toast.error(t("redeem_error"));
    } finally {
      setRedeeming(false);
    }
  };

  const bullets = [t("plan_b1"), t("plan_b2"), t("plan_b3"), t("plan_b4")];

  return (
    <div data-testid="billing-tab" className="space-y-4">
      <div className="bg-zinc-900 border-2 border-amber-500 rounded-2xl p-6">
        <div className="flex items-center gap-2 text-amber-500">
          <Crown size={22} weight="duotone" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">{t("bill_plan")}</span>
        </div>
        <p className="mt-3">
          <span className="font-display text-4xl font-black text-zinc-50" data-testid="billing-price">
            {formatPrice(plan.price, isPy ? "es" : "pt")}
          </span>
          <span className="text-zinc-400">{t("per_month")}</span>
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="billing-status">
          {subscription_active ? (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400">
              {STATUS_LABELS[subscription_status] || t("bill_active")}
            </span>
          ) : (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-500/15 text-red-400">{t("bill_paused")}</span>
          )}
          {!paid && !viaCode && trial_days_left > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-500 font-semibold" data-testid="trial-days-left">
              <Hourglass size={13} weight="duotone" /> {trial_days_left} {t("trial_days_suffix")}
            </span>
          )}
          {viaCode && (
            <span className="flex items-center gap-1 text-xs text-emerald-400 font-semibold" data-testid="paid-days-left">
              <Ticket size={13} weight="duotone" /> {paid_days_left} {t("paid_days_note")}
            </span>
          )}
        </div>

        <ul className="mt-5 space-y-2 text-sm text-zinc-300">
          {bullets.map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Check size={15} weight="bold" className="text-amber-500 shrink-0" />
              {item}
            </li>
          ))}
        </ul>

        {!paid && !isPy && !pix && (
          <div className="mt-6 space-y-2">
            <button
              data-testid="pix-btn"
              onClick={startPix}
              disabled={pixLoading}
              className="w-full h-12 rounded-xl bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
            >
              <QrCode size={20} weight="duotone" />
              {pixLoading ? t("pix_generating") : t("pix_btn")}
            </button>
            <button
              data-testid="subscribe-btn"
              onClick={subscribe}
              disabled={loading}
              className="w-full h-11 rounded-xl border border-zinc-800 text-sm font-semibold text-zinc-400 hover:border-amber-500/60 hover:text-amber-500 transition-colors disabled:opacity-60"
            >
              {loading ? t("subscribing") : t("pix_or_card")}
            </button>
          </div>
        )}
        {paid && (
          <p className="mt-6 text-xs text-zinc-500 text-center" data-testid="billing-active-msg">
            {t("bill_active_msg")}
          </p>
        )}
        {isPy && !paid && payment_info && (
          <div className="mt-6 space-y-3" data-testid="py-payment-section">
            <p className="text-sm font-bold text-zinc-200">{t("py_pay_title")}</p>
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500">{t("py_pay_alias_label")}</p>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                <p className="font-mono text-sm font-bold text-amber-500 break-words min-w-0" data-testid="py-alias">{payment_info.alias}</p>
                <button
                  data-testid="copy-alias-btn"
                  onClick={copyAlias}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 border border-zinc-800 rounded-lg px-3 py-2 hover:border-amber-500/60 hover:text-amber-500 transition-colors"
                >
                  <Copy size={13} weight="bold" /> {t("py_pay_copy_alias")}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">{payment_info.price_note}{t("per_month")}</p>
            </div>
            <a
              href={`https://wa.me/${payment_info.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="py-whatsapp-btn"
              className="w-full h-12 rounded-xl bg-emerald-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors active:scale-95"
            >
              <WhatsappLogo size={20} weight="fill" /> {t("py_pay_whatsapp")}
            </a>
            <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500 mb-3">{t("py_proof_hint")}</p>
              <label className="cursor-pointer block">
                <input
                  data-testid="proof-upload-input"
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={uploadProof}
                />
                <span className="w-full h-11 rounded-lg border border-dashed border-zinc-700 text-sm font-semibold text-zinc-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors flex items-center justify-center gap-2">
                  <UploadSimple size={16} weight="bold" />
                  {uploadingProof ? t("py_proof_uploading") : t("py_proof_upload")}
                </span>
              </label>
            </div>
          </div>
        )}
      </div>

      {pix && (
        <div className="bg-zinc-900 border-2 border-emerald-500/60 rounded-2xl p-5 text-center animate-fade-up" data-testid="pix-qr-card">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-zinc-100 flex items-center gap-2">
              <QrCode size={18} className="text-emerald-400" weight="duotone" />
              {t("pix_title")} {formatPrice(pix.amount, "pt")}
            </p>
            <button
              data-testid="pix-close-btn"
              onClick={() => setPix(null)}
              className="text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              <XCircle size={20} weight="duotone" />
            </button>
          </div>
          {pix.qr_code_base64 && (
            <img
              src={`data:image/png;base64,${pix.qr_code_base64}`}
              alt="QR Code Pix"
              className="mx-auto w-56 h-56 mt-4 rounded-lg bg-white p-2"
              data-testid="pix-qr-image"
            />
          )}
          <p className="text-sm text-zinc-400 mt-4">{t("pix_scan")}</p>
          <div className="mt-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3 max-h-20 overflow-y-auto">
            <p className="text-[11px] font-mono text-zinc-500 break-all text-left" data-testid="pix-code-text">{pix.qr_code}</p>
          </div>
          <button
            data-testid="pix-copy-btn"
            onClick={copyPix}
            className="mt-3 w-full h-11 rounded-lg bg-emerald-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors active:scale-95"
          >
            <Copy size={16} weight="bold" /> {t("pix_copy")}
          </button>
          <p className="flex items-center justify-center gap-2 text-xs text-amber-500 font-semibold mt-4" data-testid="pix-waiting">
            <span className="h-3 w-3 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            {t("pix_waiting")}
          </p>
        </div>
      )}

      {isPy && proofs.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="my-proofs-list">
          <h3 className="text-sm font-bold text-zinc-300 mb-3">{t("py_proofs_title")}</h3>
          <div className="space-y-2">
            {proofs.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5" data-testid={`my-proof-${p.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileImage size={18} className="text-zinc-500 shrink-0" weight="duotone" />
                  <p className="text-xs text-zinc-400 truncate">
                    {new Date(p.created_at).toLocaleDateString("es-PY")}
                  </p>
                </div>
                <span
                  data-testid={`my-proof-status-${p.id}`}
                  className={`text-xs font-bold px-2 py-1 rounded-full shrink-0 ${
                    p.status === "aprovado"
                      ? "bg-emerald-500/15 text-emerald-400"
                      : p.status === "recusado"
                        ? "bg-red-500/15 text-red-400"
                        : "bg-amber-500/15 text-amber-500"
                  }`}
                >
                  {p.status === "aprovado" ? t("py_proof_approved") : p.status === "recusado" ? t("py_proof_rejected") : t("py_proof_pending")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!paid && (
        <form onSubmit={redeem} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid="redeem-form">
          <h3 className="text-sm font-bold text-zinc-300 flex items-center gap-2">
            <Ticket size={16} className="text-amber-500" weight="duotone" /> {t("redeem_title")}
          </h3>
          <div className="flex gap-2">
            <input
              data-testid="redeem-code-input"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder={t("redeem_ph")}
              className="flex-1 h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 uppercase focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors"
            />
            <button
              data-testid="redeem-submit-btn"
              type="submit"
              disabled={redeeming}
              className="h-11 px-5 rounded-lg bg-amber-500 text-zinc-950 font-bold hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60"
            >
              {redeeming ? t("redeeming") : t("redeem_btn")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
